import { useEffect, useState } from 'react';
import { useClientAuthStore } from '../stores';
import clientDataService from '../services/ClientDataService';
import './Client.css';

function ClientPortal() {
  const { client, logout, isAuthenticated, initialize } = useClientAuthStore();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initialize authentication state on mount
    if (!isAuthenticated) {
      initialize();
    }
  }, [isAuthenticated, initialize]);

  useEffect(() => {
    // Fetch client projects when authenticated
    if (isAuthenticated) {
      fetchClientProjects();
    }
  }, [isAuthenticated]);

  const fetchClientProjects = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const clientProjects = await clientDataService.getClientProjects();
      setProjects(Array.isArray(clientProjects) ? clientProjects : []);
    } catch (err) {
      console.error('Error fetching client projects:', err);
      setError(err.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/client/login';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'ongoing':
        return 'status-badge ongoing';
      case 'completed':
        return 'status-badge completed';
      case 'pending':
        return 'status-badge pending';
      default:
        return 'status-badge';
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Please log in to access your portal.
      </div>
    );
  }

  return (
    <div className="client-portal-container">
      <header className="client-portal-header">
        <div className="client-portal-header-content">
          <h1>Client Portal</h1>
          <div className="client-portal-header-actions">
            <span className="client-portal-welcome">Welcome, {client?.phone || 'Client'}</span>
            <button onClick={handleLogout} className="client-portal-logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="client-portal-content">
        <div className="client-portal-section">
          <h2>Your Information</h2>
          <div className="client-portal-info">
            <div className="client-info-item">
              <label>Phone Number</label>
              <p>{client?.phone || 'N/A'}</p>
            </div>
            <div className="client-info-item">
              <label>Login Time</label>
              <p>
                {client?.loginTime 
                  ? new Date(client.loginTime).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="client-portal-section">
          <div className="client-projects-header">
            <h2>Your Projects ({projects.length})</h2>
            <button 
              onClick={fetchClientProjects} 
              className="client-refresh-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>

          {isLoading ? (
            <div className="client-loading">
              <p>Loading your projects...</p>
            </div>
          ) : error ? (
            <div className="client-error">
              <p>⚠️ {error}</p>
              <button onClick={fetchClientProjects} className="client-retry-btn">
                Try Again
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div className="client-empty-state">
              <p>You don't have any projects assigned yet.</p>
              <p className="client-empty-subtitle">Projects will appear here once they are assigned to you.</p>
            </div>
          ) : (
            <div className="client-projects-grid">
              {projects.map(project => (
                <div key={project.id} className="client-project-card">
                  <div className="client-project-header">
                    <h3>{project.project_title}</h3>
                    <span className={getStatusBadgeClass(project.status)}>
                      {project.status || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="client-project-details">
                    <div className="client-project-detail-item">
                      <label>Address</label>
                      <p>{project.address || 'N/A'}</p>
                    </div>
                    
                    <div className="client-project-detail-row">
                      <div className="client-project-detail-item">
                        <label>Start Date</label>
                        <p>{formatDate(project.start_date)}</p>
                      </div>
                      <div className="client-project-detail-item">
                        <label>Expected Completion</label>
                        <p>{formatDate(project.expected_completion_date)}</p>
                      </div>
                    </div>

                    {project.completion_date && (
                      <div className="client-project-detail-item">
                        <label>Completion Date</label>
                        <p>{formatDate(project.completion_date)}</p>
                      </div>
                    )}

                    <div className="client-project-detail-row">
                      <div className="client-project-detail-item">
                        <label>Budget</label>
                        <p className="client-project-budget">₹{project.budget?.toLocaleString('en-IN') || 'N/A'}</p>
                      </div>
                      <div className="client-project-detail-item">
                        <label>Progress</label>
                        <div className="client-project-progress">
                          <div className="client-progress-bar">
                            <div 
                              className="client-progress-fill" 
                              style={{ width: `${project.progress || 0}%` }}
                            ></div>
                          </div>
                          <span className="client-progress-text">{project.progress || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientPortal;



